import { useParams, useSearchParams } from "react-router-dom";

const formatActivityName = (activityId) => {
	if (!activityId) return "your activity";

	return activityId
		.split("-")
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
};

const pageStyles = {
	shell: {
		minHeight: "100vh",
		padding: "clamp(1rem, 2vw, 1.5rem)",
		background:
			"radial-gradient(circle at top left, rgba(175, 215, 194, 0.75), transparent 36%), radial-gradient(circle at bottom right, rgba(162, 199, 233, 0.6), transparent 30%), linear-gradient(180deg, #f8fcfd 0%, #eef5f7 100%)",
		color: "var(--ink)",
		display: "grid",
		placeItems: "center",
	},
	card: {
		width: "min(920px, 100%)",
		borderRadius: "32px",
		border: "1px solid var(--hairline)",
		background: "rgba(255, 255, 255, 0.82)",
		boxShadow: "0 24px 70px rgba(14, 26, 34, 0.12)",
		overflow: "hidden",
		backdropFilter: "blur(14px)",
	},
	grid: {
		display: "flex",
		gap: "0",
		width: "100%",
	},
	hero: {
		padding: "clamp(1.5rem, 4vw, 3.2rem)",
		position: "relative",
		background:
			"linear-gradient(135deg, rgba(162, 199, 233, 0.38), rgba(175, 215, 194, 0.38))",
	},
	panel: {
		padding: "clamp(1.5rem, 4vw, 3.2rem)",
		background: "linear-gradient(180deg, #ffffff 0%, #f8fbfc 100%)",
	},
	badge: {
		display: "inline-flex",
		alignItems: "center",
		gap: ".5rem",
		padding: ".5rem .85rem",
		borderRadius: "999px",
		background: "rgba(255, 255, 255, 0.75)",
		border: "1px solid rgba(14, 26, 34, 0.08)",
		fontSize: ".9rem",
		fontWeight: 700,
		color: "var(--ink-muted)",
	},
	title: {
		margin: "1rem 0 0.85rem",
		fontSize: "clamp(2.25rem, 5vw, 4rem)",
		lineHeight: 1.03,
		letterSpacing: "-0.03em",
	},
	intro: {
		fontSize: "1.06rem",
		lineHeight: 1.7,
		color: "var(--ink-muted)",
		maxWidth: "28ch",
	},
	detailStack: {
		display: "grid",
		gap: "1rem",
		width: "100%",
		minWidth: 0,
	},
	detailCard: {
		borderRadius: "20px",
		padding: "1rem 1.1rem",
		background: "#fff",
		border: "1px solid var(--hairline)",
		boxShadow: "0 10px 28px rgba(14, 26, 34, 0.06)",
		minWidth: 0,
		width: "100%",
	},
	detailLabel: {
		fontSize: ".84rem",
		textTransform: "uppercase",
		letterSpacing: ".12em",
		color: "#5f7380",
		marginBottom: ".4rem",
		fontWeight: 800,
	},
	detailValue: {
		fontSize: "1.1rem",
		fontWeight: 700,
		color: "var(--ink)",
	},
	joinButton: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: ".65rem",
		width: "100%",
		borderRadius: "16px",
		padding: "1rem 1.2rem",
		background: "linear-gradient(135deg, var(--c1), var(--c3))",
		color: "var(--ink)",
		fontSize: "1.02rem",
		fontWeight: 800,
		cursor: "pointer",
		boxShadow: "0 16px 30px rgba(143, 191, 194, 0.28)",
		border: "1px solid rgba(14, 26, 34, 0.08)",
		minWidth: 0,
	},
	subtle: {
		marginTop: ".85rem",
		fontSize: ".95rem",
		color: "#627680",
		textAlign: "center",
	},
};

export default function ActivityInvitePage() {
    const { activityId } = useParams();
    const [searchParams] = useSearchParams();

    const urlParams = new URLSearchParams(window.location.search);
    const extractedActivityName = urlParams.get("activity") || formatActivityName(activityId);
    const extractedHostName = urlParams.get("host") || "";
    const activityName = extractedActivityName;
    const location = searchParams.get("location") || "[location]";
    const time = searchParams.get("time") || "[time]";
	const inviteCode = searchParams.get("invite") || activityId || "BFF-APP";
	
    // --- THE DEEP LINK & CLIPBOARD LOGIC ---
    const handleJoinClick = async () => {
        // 1. Try to copy a unique token to the clipboard for first-time installers
        try {
            const payload = {
                type: "activity_invite",
                id: activityId,
                name: extractedActivityName,
                hostName: extractedHostName,
            };
            const clipboardString = `bff_invite:${JSON.stringify(payload)}`;
            await navigator.clipboard.writeText(clipboardString);
        } catch (err) {
            console.error('Clipboard fallback tracking failed', err);
        }

        // 2. Fire custom scheme to try and instantly open the React Native app
        window.location.href = `befitfriends://activity/${activityId}`;

        // 3. Fallback: If they don't have the app, wait 1.5 seconds and bounce to the store
        setTimeout(() => {
            const isAndroid = /Android/i.test(navigator.userAgent);
            if (isAndroid) {
                window.location.href = "https://play.google.com/store/apps/details?id=com.befitfriends.app"; // Update with real ID
            } else {
                window.location.href = "https://apps.apple.com/app/id6766742055"; // This is real id
            }
        }, 1500);
    };

    return (
		<main style={pageStyles.shell} className="activity-invite-shell">
		    <section style={pageStyles.card} aria-labelledby="invite-title" className="activity-invite-card">
		        <div style={pageStyles.grid} className="activity-invite-layout">
		            <div style={pageStyles.hero} className="activity-invite-hero">
                        <div style={pageStyles.badge}>
                            <span aria-hidden="true">🤝</span>
                            <span>BeFitFriends invite</span>
                        </div>

		                <h1 id="invite-title" style={pageStyles.title} className="activity-invite-title">
                            You have been invited to {activityName}!
                        </h1>

                        <p style={pageStyles.intro}>
                            Join the
                            group on BeFitFriends to connect with everyone going!
                        </p>
                    </div>

		            <div style={pageStyles.panel} className="activity-invite-panel">
		                <div style={pageStyles.detailStack} className="activity-invite-details">
		            
		                    <div style={pageStyles.detailCard} className="activity-invite-detail-card">
                                <div style={pageStyles.detailLabel}>Location</div>
                                <div style={pageStyles.detailValue}>{location}</div>
                            </div>

		                    <div style={pageStyles.detailCard} className="activity-invite-detail-card">
                                <div style={pageStyles.detailLabel}>Time</div>
                                <div style={pageStyles.detailValue}>{time}</div>
                            </div>

		                    <div style={pageStyles.detailCard} className="activity-invite-detail-card">
                                <div style={pageStyles.detailLabel}>Invite code</div>
                                <div style={pageStyles.detailValue}>{inviteCode}</div>
                            </div>

                            {/* Trigger the handleJoinClick on click */}
		                    <div onClick={handleJoinClick} style={pageStyles.joinButton} className="activity-invite-join-button">
                                <span>Join on BeFitFriends</span>
                                <span aria-hidden="true">→</span>
                            </div>

                            <div style={pageStyles.subtle}>
                                Tap the button to open on the BeFitFriends app
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
